import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';
import SafeIcon from '../common/SafeIcon';
import { FiSend, FiPaperclip, FiZap, FiCheckCircle } from 'react-icons/fi';

export default function Cockpit({ ticket, onReply }) {
  const [replyText, setReplyText] = useState('');
  const [isResolving, setIsResolving] = useState(false);

  if (!ticket) return (
    <div className="flex-1 flex items-center justify-center bg-navy-900/50">
      <div className="text-center text-slate-500">
        <SafeIcon icon={FiZap} className="text-4xl mx-auto mb-4 opacity-20" />
        <p>Select a ticket to enter the cockpit</p>
      </div>
    </div>
  );

  const handleSend = () => {
    if (!replyText.trim()) return;
    onReply(ticket.id, replyText);
    setReplyText('');
  };

  return (
    <div className="flex-1 flex flex-col h-[calc(100vh-4rem)] bg-navy-900 relative">
      {/* Header */}
      <div className="h-16 border-b border-navy-700 flex items-center justify-between px-6 bg-navy-800/80 backdrop-blur-sm z-10">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-3">
            {ticket.subject}
            <span className="text-xs font-mono bg-navy-700 px-2 py-1 rounded text-slate-300">
              {ticket.id}
            </span>
          </h2>
          <div className="flex gap-2 mt-1">
            {ticket.tags.map(tag => (
              <span key={tag} className="text-[10px] text-cyan-neon bg-cyan-neon/10 px-1.5 rounded">#{tag}</span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setIsResolving(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-500/10 hover:bg-green-500/20 text-green-400 border border-green-500/30 rounded-lg text-sm transition-all"
          >
            <SafeIcon icon={FiCheckCircle} />
            Resolve
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6 scrollbar-hide">
        <AnimatePresence>
          {ticket.messages.map((msg, idx) => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className={cn(
                "max-w-[80%]",
                msg.sender === 'agent' ? "ml-auto" : "mr-auto",
                msg.sender === 'system' ? "mx-auto text-center max-w-full" : ""
              )}
            >
              {msg.sender === 'system' ? (
                <div className="inline-block px-4 py-1.5 rounded-full bg-priority-red/10 border border-priority-red/20 text-priority-red text-xs font-mono">
                  SYSTEM LOG: {msg.text}
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  <div className={cn(
                    "flex items-center gap-2 text-xs text-slate-500",
                    msg.sender === 'agent' ? "flex-row-reverse" : "flex-row"
                  )}>
                    <span>{msg.sender === 'agent' ? 'You' : ticket.customer.name}</span>
                    <span>•</span>
                    <span>{format(new Date(msg.timestamp), 'HH:mm')}</span>
                  </div>
                  <div className={cn(
                    "p-4 rounded-2xl text-sm leading-relaxed",
                    msg.sender === 'agent' 
                      ? "bg-cyan-neon/10 text-cyan-50 border border-cyan-neon/20 rounded-tr-sm" 
                      : "bg-navy-700 text-slate-200 border border-navy-600 rounded-tl-sm"
                  )}>
                    {msg.text}
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Composer */}
      <div className="p-4 bg-navy-800 border-t border-navy-700">
        <div className="glass-panel rounded-xl flex flex-col p-2">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Type your reply... Use / for macros"
            className="w-full bg-transparent border-none resize-none focus:ring-0 text-white p-3 min-h-[80px] text-sm"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <div className="flex items-center justify-between px-3 pb-2 pt-1">
            <div className="flex gap-2 text-slate-400">
              <button className="p-2 hover:text-cyan-neon hover:bg-navy-700 rounded-lg transition-colors">
                <SafeIcon icon={FiPaperclip} />
              </button>
              <button className="p-2 hover:text-cyan-neon hover:bg-navy-700 rounded-lg transition-colors">
                <SafeIcon icon={FiZap} />
              </button>
            </div>
            <button
              onClick={handleSend}
              disabled={!replyText.trim()}
              className="flex items-center gap-2 px-6 py-2 bg-cyan-neon text-navy-900 font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-cyan-400 transition-colors shadow-[0_0_15px_rgba(0,240,255,0.3)] hover:shadow-[0_0_25px_rgba(0,240,255,0.5)]"
            >
              Send <SafeIcon icon={FiSend} className="text-sm" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}